import React from "react";
import { Link } from "react-router-dom";
import classes from "./record.module.scss";

export type RecordTone = "default" | "success" | "warning" | "danger" | "info";

/** One figure in a record header's stat strip. */
export interface RecordStat {
    key: string;
    /** Overline label — "Open missions". */
    label: string;
    value: React.ReactNode;
    /** The sub line under the value — "1 overdue", "in 8 days". */
    hint?: React.ReactNode;
    /** Colours the hint: warning / danger when something is late or overdue. */
    tone?: RecordTone;
    /** Colours the value itself (e.g. a rating). */
    valueTone?: RecordTone;
    /** Hover text — what the number counts and what clicking opens. */
    tooltip: string;
    /** In-app route behind the stat. */
    to?: string;
    /** Opens the tab or drawer behind the stat. */
    onClick?: () => void;
    /** Draws the tile in the danger tone — a figure that needs action now. */
    highlight?: boolean;
}

export const toneClass = (tone: RecordTone | undefined): string => {
    switch (tone) {
        case "success": return classes.toneSuccess;
        case "warning": return classes.toneWarning;
        case "danger": return classes.toneDanger;
        case "info": return classes.toneInfo;
        default: return "";
    }
};

const StatBody: React.FC<{ stat: RecordStat }> = ({ stat }) => (
    <>
        <span className={classes.statLabel}>{stat.label}</span>
        <span className={`${classes.statValue} ${toneClass(stat.valueTone)}`}>{stat.value}</span>
        {stat.hint !== undefined && stat.hint !== null && stat.hint !== "" && (
            <span className={`${classes.statHint} ${toneClass(stat.tone)}`}>{stat.hint}</span>
        )}
    </>
);

/**
 * One KPI tile. The only KPI-tile markup in the app: StatStrip renders these,
 * and pages that compose their own strip (hubs, dashboards, reports) put them
 * inside `<StatStrip standalone>` as children. A link with `to`, a button with
 * `onClick`, plain otherwise.
 */
export const StatTile: React.FC<{ stat: RecordStat }> = ({ stat }) => {
    const className = `${classes.stat} ${stat.highlight ? classes.statHighlight : ""}`;
    if (stat.to) {
        return (
            <Link className={className} to={stat.to} title={stat.tooltip}>
                <StatBody stat={stat} />
            </Link>
        );
    }
    if (stat.onClick) {
        return (
            <button type="button" className={`btn-unstyled ${className}`} title={stat.tooltip} onClick={stat.onClick}>
                <StatBody stat={stat} />
            </button>
        );
    }
    return (
        <div className={className} title={stat.tooltip}>
            <StatBody stat={stat} />
        </div>
    );
};

/** A placeholder tile while the figures load. */
export const StatTilePlaceholder: React.FC = () => (
    <div className={classes.stat} aria-hidden="true">
        <span className={classes.statLabel}>Loading…</span>
        <span className={classes.statValue}>-</span>
    </div>
);

interface StatStripProps {
    stats?: RecordStat[];
    /** Tiles composed by the caller (StatTile, or a wrapper of it) instead of `stats`. */
    children?: React.ReactNode;
    /** Shows this many placeholder tiles instead of the stats. */
    loading?: number;
    /** Outside a RecordHeader the strip draws its own card. */
    standalone?: boolean;
    className?: string;
}

/**
 * The record's key figures (4–7). Each stat is a link or a button to the tab
 * or drawer behind it. Renders nothing without stats or children.
 */
const StatStrip: React.FC<StatStripProps> = ({ stats, children, loading, standalone, className }) => {
    const hasChildren = React.Children.toArray(children).length > 0;
    if (!loading && !hasChildren && (!stats || stats.length === 0)) return null;
    return (
        <div className={`${classes.stats} ${standalone ? classes.statsStandalone : ""} ${className ?? ""}`}>
            {loading
                ? [...Array(loading)].map((_, i) => <StatTilePlaceholder key={i} />)
                : <>
                    {stats?.map((stat) => <StatTile key={stat.key} stat={stat} />)}
                    {children}
                </>}
        </div>
    );
};

export default StatStrip;
